/**
 * Cart availability guard.
 *
 * Shopify's cart never re-validates existing line items against live stock —
 * a line added while a variant was in stock stays in the cart, at full price
 * and quantity, even after that variant is marked out of stock. Nothing
 * surfaces the problem until checkout, which rejects the whole order with a
 * generic "this item is out of stock" error for a product the customer may
 * never have consciously interacted with that session.
 *
 * This guard proactively checks every cart line against the live product
 * data and removes any line whose variant is no longer available, showing a
 * short localized notice, at three points: cart drawer open, /cart page
 * load, and just before the checkout button is allowed to submit.
 */
(function () {
  'use strict';

  if (window.__nailoraCartGuardReady) return;
  window.__nailoraCartGuardReady = true;

  function cartRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function buildRemovedMessage(title) {
    var template = (window.theme && window.theme.cartStrings && window.theme.cartStrings.itemRemovedUnavailable) || '';
    return template.replace('[product]', escapeHtml(title));
  }

  function showNotice(messages) {
    if (!messages.length) return;
    var containers = document.querySelectorAll('.cart-availability-notice');
    containers.forEach(function (container) {
      container.innerHTML = messages
        .map(function (m) { return '<p>' + m + '</p>'; })
        .join('');
      container.hidden = false;
    });
  }

  function clearNotice() {
    document.querySelectorAll('.cart-availability-notice').forEach(function (container) {
      container.hidden = true;
      container.innerHTML = '';
    });
  }

  var PENDING_NOTICE_KEY = 'nailoraCartAvailabilityNotice';

  function stashNoticeForReload(messages) {
    try {
      sessionStorage.setItem(PENDING_NOTICE_KEY, JSON.stringify(messages));
    } catch (e) { /* storage unavailable, message just won't survive the reload */ }
  }

  function showStashedNoticeIfAny() {
    var raw;
    try {
      raw = sessionStorage.getItem(PENDING_NOTICE_KEY);
      sessionStorage.removeItem(PENDING_NOTICE_KEY);
    } catch (e) { return; }
    if (!raw) return;
    try {
      var messages = JSON.parse(raw);
      if (messages && messages.length) showNotice(messages);
    } catch (e) { /* ignore malformed stash */ }
  }

  /**
   * Fetches live availability for every distinct product handle currently in
   * the cart, in parallel, and returns a map of variant_id -> available.
   * Variant IDs it couldn't verify (network error) are left out of the map
   * entirely, so uncertain lines are never removed.
   */
  function fetchAvailabilityMap(handles) {
    var map = {};
    return Promise.all(
      handles.map(function (handle) {
        return fetch(cartRoot() + '/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            if (!product || !product.variants) return;
            product.variants.forEach(function (v) {
              map[v.id] = v.available;
            });
          })
          .catch(function () {
            /* leave this product's variants unverified rather than guess */
          });
      })
    ).then(function () {
      return map;
    });
  }

  /**
   * Checks the live cart against live product availability and removes any
   * stale line (quantity -> 0 via /cart/update.js). Resolves with
   * { removed: [{title, variantTitle}], cart } — cart is the fresh state
   * returned by Shopify once removal has happened, or the untouched cart if
   * nothing was stale.
   */
  function checkAndRemoveStaleLines() {
    return fetch(cartRoot() + '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          return { removed: [], cart: cart };
        }

        var handles = Array.from(new Set(cart.items.map(function (i) { return i.handle; })));

        return fetchAvailabilityMap(handles).then(function (availability) {
          var staleItems = cart.items.filter(function (item) {
            return Object.prototype.hasOwnProperty.call(availability, item.variant_id) &&
              availability[item.variant_id] === false;
          });

          if (!staleItems.length) {
            return { removed: [], cart: cart };
          }

          var updates = {};
          staleItems.forEach(function (item) {
            updates[item.key] = 0;
          });

          return fetch(cartRoot() + '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              updates: updates,
              sections: ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
              sections_url: window.location.pathname
            })
          })
            .then(function (r) { return r.json(); })
            .then(function (updatedCart) {
              return { removed: staleItems, cart: updatedCart };
            });
        });
      })
      .catch(function (e) {
        console.error('[cart-availability-guard]', e);
        return { removed: [], cart: null };
      });
  }

  function applyRemovalResult(result) {
    if (!result.removed.length) return result;

    var messages = result.removed.map(function (item) {
      // item.title already bakes the variant name in for most products, so
      // build the label from product_title + variant_title explicitly to
      // avoid duplicating it (e.g. "Hand Gel Pads - 5 sheets — 5 sheets").
      var label = item.variant_title && item.variant_title !== 'Default Title'
        ? item.product_title + ' — ' + item.variant_title
        : item.product_title;
      return buildRemovedMessage(label);
    });

    // Refresh whatever cart UI is on screen with the post-removal state.
    var miniCart = document.getElementById('mini-cart');
    if (miniCart && result.cart && result.cart.sections && typeof miniCart.renderContents === 'function') {
      // renderContents() also re-opens the drawer, which we don't want here
      // if the drawer wasn't already the trigger — so patch sections by hand.
      // Patch first: the fresh section HTML contains its own (empty, hidden)
      // notice element, which would wipe out the message if we set it first.
      miniCart.getSectionsToRender().forEach(function (section) {
        var el = document.getElementById(section.id);
        if (el && result.cart.sections[section.id]) {
          el.innerHTML = miniCart.getSectionInnerHTML(result.cart.sections[section.id], section.selector);
        }
      });
      showNotice(messages);
    } else if (document.body.classList.contains('template-cart') || window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
      // Full /cart page: a reload is the simplest correct way to refresh
      // totals/footer state, but it would also wipe the notice we just
      // rendered — stash it so it survives and shows after reload.
      stashNoticeForReload(messages);
      window.location.reload();
    } else {
      showNotice(messages);
    }

    return result;
  }

  function runCheck() {
    return checkAndRemoveStaleLines().then(applyRemovalResult);
  }

  // ── Trigger 1: cart drawer opens ──────────────────────────────────────
  var drawerDetails = document.querySelector('cart-drawer details.cart-drawer-container');
  if (drawerDetails) {
    var wasOpen = drawerDetails.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = drawerDetails.hasAttribute('open');
      if (isOpen && !wasOpen) {
        clearNotice();
        runCheck();
      }
      wasOpen = isOpen;
    }).observe(drawerDetails, { attributes: true, attributeFilter: ['open'] });
  }

  // ── Trigger 2: the full /cart page itself ─────────────────────────────
  if (window.location.pathname.replace(/\/$/, '').match(/\/cart$/)) {
    function onCartPageReady() {
      showStashedNoticeIfAny();
      runCheck();
    }
    document.addEventListener('DOMContentLoaded', onCartPageReady);
    if (document.readyState !== 'loading') onCartPageReady();
  }

  // ── Trigger 3: just before checkout submits ───────────────────────────
  function guardCheckoutForm(form) {
    if (!form || form.dataset.availabilityGuardBound) return;
    form.dataset.availabilityGuardBound = 'true';
    form.addEventListener('submit', function (e) {
      var submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset.availabilityGuardPassed === 'true') {
        form.dataset.availabilityGuardPassed = '';
        return; // already checked this exact submission, let it through
      }
      e.preventDefault();
      runCheck().then(function (result) {
        if (result.removed.length) {
          // Cart changed under the customer — let them see the notice and
          // the updated totals before trying checkout again.
          return;
        }
        form.dataset.availabilityGuardPassed = 'true';
        if (form.requestSubmit) {
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      });
    });
  }

  document.querySelectorAll('form[action*="/cart"]#cart, form#cart').forEach(guardCheckoutForm);

  // Cart forms can be re-rendered (AJAX section updates) after this script
  // first runs, so keep watching for new ones.
  new MutationObserver(function () {
    document.querySelectorAll('form[action*="/cart"]#cart, form#cart').forEach(guardCheckoutForm);
  }).observe(document.body, { childList: true, subtree: true });
})();
